import { apiFetch } from './api';
import { uploadFileAndGetUrl } from './coa';

// Core Licence Request APIs
export function listLicences({ status = '', search = '', page = 1, limit = 10, reviewerId, approverId, assignedUserId, customerId, assignedToMe, myReviewQueue, myApprovalQueue, attentionOnly, submittedFrom, submittedTo } = {}) {
  const params = new URLSearchParams();
  if (status && status !== 'All') params.set('status', status);
  if (search) params.set('search', search);
  if (reviewerId) params.set('reviewerId', reviewerId);
  if (approverId) params.set('approverId', approverId);
  if (assignedUserId) params.set('assignedUserId', assignedUserId);
  if (customerId) params.set('customerId', customerId);
  if (assignedToMe === true || assignedToMe === 'true') params.set('assignedToMe', 'true');
  if (myReviewQueue === true || myReviewQueue === 'true') params.set('myReviewQueue', 'true');
  if (myApprovalQueue === true || myApprovalQueue === 'true') params.set('myApprovalQueue', 'true');
  if (attentionOnly === true || attentionOnly === 'true') params.set('attentionOnly', 'true');
  if (submittedFrom && String(submittedFrom).trim()) params.set('submittedFrom', String(submittedFrom).trim());
  if (submittedTo && String(submittedTo).trim()) params.set('submittedTo', String(submittedTo).trim());
  params.set('page', String(page));
  params.set('limit', String(limit));
  const q = params.toString() ? `?${params.toString()}` : '';
  return apiFetch(`/api/admin/licences${q}`);
}

export function getLicenceStats() {
  return apiFetch('/api/admin/licences/stats');
}

export function getLicence(id) {
  return apiFetch(`/api/admin/licences/${id}`);
}

export function createLicence(payload) {
  return apiFetch('/api/admin/licences', { method: 'POST', body: payload });
}

export function updateLicence(id, payload) {
  return apiFetch(`/api/admin/licences/${id}`, { method: 'PATCH', body: payload });
}

export function deleteLicence(id) {
  return apiFetch(`/api/admin/licences/${id}`, { method: 'DELETE' });
}

export function assignStaff(licenceId, { reviewerId, approverId }) {
  return apiFetch(`/api/admin/licences/${licenceId}/assign`, { method: 'POST', body: { reviewerId, approverId } });
}

export function updateLicenceStatus(licenceId, status) {
  return apiFetch(`/api/admin/licences/${licenceId}/status`, { method: 'POST', body: { status } });
}

export function updatePaymentStatus(licenceId, status) {
  return apiFetch(`/api/admin/licences/${licenceId}/payment`, { method: 'PATCH', body: { status } });
}

export function archiveLicence(licenceId) {
  return apiFetch(`/api/admin/licences/${licenceId}/archive`, { method: 'POST' });
}

// Support Documents
export function listDocuments(licenceId) {
  return apiFetch(`/api/admin/licences/${licenceId}/documents`);
}

export function createDocument(licenceId, payload) {
  return apiFetch(`/api/admin/licences/${licenceId}/documents`, { method: 'POST', body: payload });
}

export function updateDocument(docId, payload) {
  return apiFetch(`/api/admin/licences/documents/${docId}`, { method: 'PATCH', body: payload });
}

export function deleteDocument(docId) {
  return apiFetch(`/api/admin/licences/documents/${docId}`, { method: 'DELETE' });
}

export function lockDocument(licenceId, docId) {
  return apiFetch(`/api/admin/licences/${licenceId}/documents/${docId}/lock`, { method: 'POST' });
}

export function unlockDocument(licenceId, docId) {
  return apiFetch(`/api/admin/licences/${licenceId}/documents/${docId}/unlock`, { method: 'POST' });
}

// Translations
export function listTranslations(licenceId) {
  return apiFetch(`/api/admin/licences/${licenceId}/translations`);
}

export function createTranslation(licenceId, payload) {
  return apiFetch(`/api/admin/licences/${licenceId}/translations`, { method: 'POST', body: payload });
}

export function updateTranslation(translationId, payload) {
  return apiFetch(`/api/admin/licences/translations/${translationId}`, { method: 'PATCH', body: payload });
}

export function deleteTranslation(translationId) {
  return apiFetch(`/api/admin/licences/translations/${translationId}`, { method: 'DELETE' });
}

// FAQs
export function listFAQs(licenceId) {
  return apiFetch(`/api/admin/licences/${licenceId}/faqs`);
}

export function createFAQ(licenceId, payload) {
  return apiFetch(`/api/admin/licences/${licenceId}/faqs`, { method: 'POST', body: payload });
}

export function updateFAQ(faqId, payload) {
  return apiFetch(`/api/admin/licences/faqs/${faqId}`, { method: 'PATCH', body: payload });
}

export function deleteFAQ(faqId) {
  return apiFetch(`/api/admin/licences/faqs/${faqId}`, { method: 'DELETE' });
}

// Fees
export function getFees(licenceId) {
  return apiFetch(`/api/admin/licences/${licenceId}/fees`);
}

export function updateFees(licenceId, payload) {
  return apiFetch(`/api/admin/licences/${licenceId}/fees`, { method: 'PUT', body: payload });
}

// Terms
export function getTerms(licenceId) {
  return apiFetch(`/api/admin/licences/${licenceId}/terms`);
}

export function updateTerms(licenceId, { termsContent, permissionsToUse, restrictions }) {
  return apiFetch(`/api/admin/licences/${licenceId}/terms`, { method: 'PUT', body: { termsContent, permissionsToUse, restrictions } });
}

// Timeline
export function getTimeline(licenceId) {
  return apiFetch(`/api/admin/licences/${licenceId}/timeline`);
}

export function addTimelineEntry(licenceId, payload) {
  return apiFetch(`/api/admin/licences/${licenceId}/timeline`, { method: 'POST', body: payload });
}

// Additional Info Request
export function requestAdditionalInfo(licenceId, payload) {
  return apiFetch(`/api/admin/licences/${licenceId}/additional-info-request`, {
    method: 'POST',
    body: payload
  });
}

// Expiration
export function setExpiration(licenceId, expiresAt) {
  return apiFetch(`/api/admin/licences/${licenceId}/expiration`, {
    method: 'POST',
    body: { expiresAt }
  });
}

// Reviewer/Approver Actions
export function markAsReviewed(licenceId) {
  return apiFetch(`/api/admin/licences/${licenceId}/mark-reviewed`, {
    method: 'POST'
  });
}

export function sendBackToReviewer(licenceId, { reason, comments }) {
  return apiFetch(`/api/admin/licences/${licenceId}/send-back-to-reviewer`, {
    method: 'POST',
    body: { reason, comments }
  });
}

export function listAdditionalDocuments(licenceId) {
  return apiFetch(`/api/admin/licences/${licenceId}/additional-documents`);
}

export function addNote(licenceId, note) {
  return apiFetch(`/api/admin/licences/${licenceId}/notes`, { 
    method: 'POST', 
    body: { note } 
  });
}

// Queue Counts
export function getReviewQueueCount() {
  return apiFetch('/api/admin/licences/review-queue-count');
}

export function getApprovalQueueCount() {
  return apiFetch('/api/admin/licences/approval-queue-count');
}

export function getAwaitingReviewCount() {
  return apiFetch('/api/admin/licences/awaiting-review-count');
}

// Helper function for file uploads (reuse from coa.js)
export { uploadFileAndGetUrl } from './coa';




